<template>
  <div class="nosto-pagination container">
    <btn
      v-if="page !== totalPages && products.length && $variable('settings.pagination.useLoadMore')"
      class="nosto-pagination__load-button"
      :label="$string('collection.pagination.load_more')"
      :show-icon="false"
      :show-state-icon="state === 'loading'"
      type="button"
      @click="handleLoadMore"
    >
      <template #stateIcon>
        <icon-loading />
      </template>
    </btn>

    <p
      v-if="products.length"
      class="nosto-pagination__product-count text-body-2xs text-body-s-desktop"
      v-html="countMessage"
    />

    <div
      v-if="totalPages > 1 && !$variable('settings.pagination.useLoadMore')"
      class="nosto-pagination__inner"
    >
      <btn
        class="nosto-pagination__previous"
        :class="{ [$class('disabled')]: page === 1 }"
        :label="$string('general.pagination.previous')"
        modifiers="text"
        :show-icon="false"
        :url="buildHref(page === 1 ? 1 : page - 1)"
        @click.prevent="handleButtonClick('prev')"
      />

      <a
        class="nosto-pagination__page text-body-s"
        :class="{ [$class('active')]: page === 1 }"
        :href="buildHref(1)"
        @click.prevent="handlePageChange(1)"
        v-text="1"
      />

      <span
        v-if="page > Math.ceil(offset / 2) + 1"
        class="nosto-pagination__space text-body-s"
      >...</span>

      <a
        v-for="index of pagesWithOffset"
        :key="index"
        class="nosto-pagination__page text-body-s"
        :class="{ [$class('active')]: page === index }"
        :href="buildHref(index)"
        @click.prevent="handlePageChange(index)"
        v-text="index"
      />

      <span
        v-if="page < totalPages - Math.floor(offset / 2) - 1"
        class="nosto-pagination__space text-body-s"
      >...</span>

      <a
        class="nosto-pagination__page text-body-s"
        :class="{ [$class('active')]: page === totalPages }"
        :href="buildHref(totalPages)"
        @click.prevent="handlePageChange(totalPages)"
        v-text="totalPages"
      />

      <btn
        v-if="page <= totalPages"
        class="nosto-pagination__next"
        :class="{ [$class('disabled')]: page === totalPages }"
        :label="$string('general.pagination.next')"
        modifiers="text"
        :show-icon="false"
        :url="buildHref(page === totalPages ? totalPages : page + 1)"
        @click.prevent="handleButtonClick('next')"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Nosto pagination (main-collection)
 * -----------------------------------------------------------------------------
 * Pagination component for nosto collection and search templates.
 *
 * @param {Boolean} nostoReady - nosto is ready.
 * @param {Number} limit - Per page.
 * @param {Number} page - Current page.
 * @param {Array} products - nosto products.
 * @param {String} state - Current state.
 * @param {Number} total - Total number of current items.
 *
 */
import Btn from '~global/btn/btn'

import IconLoading from '~icons/misc/loading.svg'

export default {
  name: 'NostoPagination',

  components: {
    Btn,
    IconLoading,
  },

  props: {
    nostoReady: {
      type: Boolean,
      default: false,
    },
    defaultLink: {
      type: String,
      default: '',
    },
    collectionLink: {
      type: String,
      default: '',
    },
    limit: {
      type: Number,
      default: 60,
    },
    page: {
      type: Number,
      default: 1,
    },
    products: {
      type: Array,
      default: () => [],
    },
    state: {
      type: String,
      default: 'loading',
    },
    total: {
      type: Number,
      default: 0,
    },
  },

  emits: {
    'choices-updated': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      offset: 3,
      pagesWithOffset: [],
      totalPages: 1,
    }
  },

  computed: {

    /**
     * Compute dynamic product count message.
     * @returns {String}
     */
    countMessage() {
      let items = '<span></span>'
      if (this.nostoReady) {
        const currentMax = this.page === this.totalPages
          ? this.total
          : this.page * this.limit
        const currentMin = currentMax - this.products.length + 1
        items = `${currentMin} - ${currentMax}`
      }

      const total = this.nostoReady
        ? this.total
        : '<span></span>'

      const type = this.$string('collection.pagination.products', { pluralise: this.total })

      return this.$string('collection.pagination.showing_item_count_html', {
        replace: {
          items,
          total,
          type,
        },
      })
    },
  },

  watch: {
    page() {
      this.calculateTotalPages()

      if (this.page > this.totalPages) {
        const url = this.collectionLink
        const cleanedUrl = url.startsWith('/') ? url.substring(1) : url
        location.href = `//${location.host}${cnvs.routes.normalisedRoot}${cleanedUrl}`
      }
    },

    total() {
      this.calculateTotalPages()
    },
  },

  mounted() {
    this.calculateTotalPages()
  },

  methods: {

    /**
     * Built location href for SEO.
     * @param {Number} page - page to add.
     * @return {String}
     */
    buildHref(page) {
      if (this.defaultLink.includes('?')) {
        return `${this.defaultLink}&page=${page}`
      }

      return `${this.defaultLink}?page=${page}`
    },

    /**
     * Calculate number of pages, create array for view.
     */
    calculateTotalPages() {
      this.totalPages = Math.ceil(this.total / this.limit)

      // Make an array of pages without first and last page link.
      const pageArray = [...Array(this.totalPages).keys()]
      pageArray.shift()
      pageArray.shift()

      const halfOffset = Math.ceil(this.offset / 2)
      let offset = this.offset - 1
      let start = 0

      if (this.page > halfOffset) {
        offset = this.offset
        start = this.page - halfOffset - 1
      }

      if (this.page > this.totalPages - halfOffset) {
        start = this.totalPages - this.offset - 1
      }

      this.pagesWithOffset = pageArray.splice(start, offset)
    },

    /**
     * Loads next page products.
     */
    handleLoadMore() {
      this.$emit('choices-updated', {
        action: 'add',
        loadMore: true,
        requestParam: `&page=${this.page + 1}`,
      })
    },

    /**
     * Handle pagination click.
     * @param {Number} index - Index of page to change to.
     */
    handlePageChange(index) {
      if (index === this.page) {
        return
      }

      this.$emit('choices-updated', {
        action: 'add',
        forceReplace: true,
        requestParam: `&page=${index}`,
      })

      this.$nextTick().then(() => {
        window.scroll({
          top: 0,
          left: 0,
          behavior: 'smooth',
        })
      })
    },

    /**
     * Update page on pagination button click.
     * @param {String} type - Direction of pagination.
     */
    handleButtonClick(type) {
      if (type === 'prev') {
        this.handlePageChange(this.page - 1)
        return
      }

      this.handlePageChange(this.page + 1)
    },
  },
}
</script>

<style lang="scss">
@import './nosto-pagination';
@import '@/components/pagination';
</style>
